import Decimal from 'decimal.js';
import { GerenciadorPrecos } from '../../../../../@types/GerenciadorPrecos';
import { IPAMaths as calc } from '../../../../../lib';

type RowData = GerenciadorPrecos.RowData;

/**
 * Calcula o CPI específico para o ExpandedRowWholesale
 * Fórmula: (preço atacado / preço concorrente) * 100
 */
export const calcWholesaleCPI = (wholesalePrice: number | null, competitorsPrice: number | null): number => {
  if (wholesalePrice === null || competitorsPrice === null || competitorsPrice === 0) {
    return 0;
  }
  return new Decimal(wholesalePrice).div(competitorsPrice).times(100).toDecimalPlaces(1).toNumber();
};

/**
 * Calcula o preço atacado a partir do CPI
 * Fórmula: (CPI / 100) * preço concorrente
 */
export const calcWholesalePriceFromCPI = (cpi: number | null, competitorsPrice: number | null): number | null => {
  if (cpi === null || competitorsPrice === null) {
    return null;
  }
  return new Decimal(cpi).div(100).times(competitorsPrice).toDecimalPlaces(2).toNumber();
};

/**
 * Calcula a margem específica para o ExpandedRowWholesale
 */
export const calcWholesaleMargin = (
  wholesalePrice: number | null,
  salesTaxPercentage: number | null,
  expensesPercent: number | null,
  cost: number | null,
): number => {
  if (wholesalePrice === null || salesTaxPercentage === null || expensesPercent === null || cost === null) {
    return 0;
  }

  const validPrice = new Decimal(wholesalePrice);
  const validSalesTaxPercentage = new Decimal(salesTaxPercentage);
  const validExpensesPercent = new Decimal(expensesPercent);
  const validCost = new Decimal(cost);

  const result = validPrice
    .minus(validPrice.times(validSalesTaxPercentage.plus(validExpensesPercent).div(100)))
    .minus(validCost)
    .div(validPrice)
    .times(100);

  return result.toDecimalPlaces(1).toNumber();
};

/**
 * Calcula a variação do CPI específica para o ExpandedRowWholesale
 * Compara o CPI atual do atacado com o CPI do varejo
 */
export const calcWholesaleCPIVariation = (wholesaleCPI: number | null, retailCPI: number | null): number | null => {
  if (wholesaleCPI === null || wholesaleCPI === undefined || retailCPI === null || retailCPI === undefined) {
    return null;
  }
  return new Decimal(wholesaleCPI).minus(retailCPI).toDecimalPlaces(1).toNumber();
};

export const handleCalcWholesalePrice = (price: number | null, rowData: RowData) => {
  const { competitorsPrice, salesTaxPercentage, expensesPercent, cost } = rowData;

  const newCompetitivenessPrice = calcWholesaleCPI(price, competitorsPrice);

  const newMargin = calcWholesaleMargin(price, salesTaxPercentage, expensesPercent, cost);

  return {
    price,
    newCompetitivenessPrice,
    newMargin,
  };
};

export const handleCalcWholesaleCPI = (newCompetitivenessPrice: number | null, rowData: RowData) => {
  const { competitorsPrice, salesTaxPercentage, expensesPercent, cost } = rowData;

  const price = calcWholesalePriceFromCPI(newCompetitivenessPrice, competitorsPrice);

  const newMargin = calcWholesaleMargin(price, salesTaxPercentage, expensesPercent, cost);

  return {
    price,
    newCompetitivenessPrice,
    newMargin,
  };
};

export const handleCalcWholesaleMargin = (newMargin: number | null, rowData: RowData) => {
  const { cost, expensesPercent, salesTaxPercentage, competitorsPrice } = rowData;

  if (newMargin === 0 || cost === 0) {
    return {
      price: 0,
      newCompetitivenessPrice: 0,
      newMargin: 0,
    };
  }

  const price = calc.precoWithMargem(newMargin, cost, expensesPercent, salesTaxPercentage);

  const newCompetitivenessPrice = calcWholesaleCPI(price, competitorsPrice);

  return {
    price,
    newCompetitivenessPrice,
    newMargin,
  };
};
